"use client";


import { Link } from "@/i18n/navigation";
import { ChampionIcon } from "@/shared/ui/data-display/champion-icon";
import { useTranslations } from "next-intl";

import { cn } from "@/shared/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/ui/data-display/avatar";
import { MEDALS } from "@/shared/lib/medal";
import { scoreColor } from "@/shared/lib/score-color";
import { SectionHeading } from "./section-heading";

/**
 * KAN-807: карточка MVP турнира с разложением балла (эпик KAN-804).
 *
 * Прежняя карточка отвечала «кто MVP» и показывала полосы факторов сбоку мелким
 * шрифтом. Здесь разложение — половина карточки: балл сам по себе ничего не
 * объясняет, а объяснение и есть то, ради чего его читают. «Почему он, а не наш
 * керри» — первый вопрос в чате после турнира.
 *
 * ГЛАВНОЕ ПРАВИЛО ЭТОЙ ВИТРИНЫ. Компонента, которой не было ни в одной игре,
 * получает в балле нейтральные 0.5 — так честнее для суммы, игрока не наказывают
 * за отсутствие поля. Но в разложении нейтраль неотличима от измеренной середины,
 * и «Вижен 50» у игрока, чей вижен не мерили, читается как факт. Поэтому бэкенд
 * помечает такие компоненты (`measured: false`, KAN-807), а витрина обязана
 * сказать это словами и не рисовать полосу.
 */

type MvpFactor = {
  key: string;
  /** Значение компоненты, 0..1. */
  value: number;
  weight: number;
  contribution: number;
  /**
   * Компонента посчитана по играм. false — значение подставлено нейтралью,
   * потому что данных не было. Поле появилось в KAN-807; у ответов старее его нет,
   * поэтому отсутствие трактуем как «измерена» — иначе на всех архивах разом
   * появилось бы «не измерено» там, где данные на самом деле есть.
   */
  measured?: boolean;
};

export type TournamentMvp = {
  userId: string;
  displayName: string;
  avatarUrl?: string | null;
  teamName?: string | null;
  games: number;
  wins: number;
  losses: number;
  winrate: number;
  avgKda: string;
  kdaRatio: number;
  topChampionName?: string | null;
  placement?: number | null;
  /** Балл 0..1 — сумма вкладов компонент. */
  score: number;
  isLowConfidence: boolean;
  isFormerMember?: boolean;
  role?: string | null;
  factors: MvpFactor[];
};

/**
 * Ключи компонент приходят с бэкенда, подписи берутся из словаря. До этой правки
 * подписи были русскими строками в коде, и на английской локали карточка MVP
 * оставалась русской целиком — вместе с местом, шапкой и оговорками.
 */
const PLACE_KEY: Record<number, string> = {
  1: "place1",
  2: "place2",
  3: "place3",
};

/** Геометрия кольца балла: радиус 52 в поле 112×112 — числа из макета. */
const RING_RADIUS = 52;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

function ScoreRing({ score }: { score: number }) {
  const t = useTranslations("TournamentStats.mvp");
  const filled = Math.max(0, Math.min(100, score));

  return (
    <div className="relative h-28 w-28 flex-none">
      <svg viewBox="0 0 112 112" className="absolute inset-0 -rotate-90">
        <circle
          cx="56"
          cy="56"
          r={RING_RADIUS}
          fill="none"
          stroke="var(--muted)"
          strokeWidth="7"
        />
        <circle
          cx="56"
          cy="56"
          r={RING_RADIUS}
          fill="none"
          stroke="var(--primary)"
          strokeWidth="7"
          strokeLinecap="round"
          strokeDasharray={`${((filled / 100) * RING_CIRCUMFERENCE).toFixed(1)} ${RING_CIRCUMFERENCE.toFixed(1)}`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-heading text-4xl font-bold leading-none tabular-nums">
          {Math.round(filled)}
        </div>
        {/* «Балл MVP», а не просто «балл»: на этой же странице у игрока есть ВТОРОЙ
            балл 0–100 — поведенческий (GL Score) в таблице и в профиле. Числа разные
            по построению и совпасть не могут, поэтому одно слово на двоих читалось
            как расхождение данных: на проде у Wilso 78 здесь и 82 там. */}
        <div className="mt-0.5 text-[9px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
          {t("scoreLabel")}
        </div>
      </div>
    </div>
  );
}

/** Показатель в шапке: подпись, крупное значение и сырая расшифровка под ним. */
function Stat({
  label,
  value,
  raw,
}: {
  label: string;
  value: string;
  raw?: string | null;
}) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.09em] text-muted-foreground">
        {label}
      </div>
      <div className="font-heading text-[22px] font-semibold leading-[1.15] tabular-nums">
        {value}
      </div>
      {raw ? (
        <div className="text-[11px] tabular-nums text-muted-foreground">{raw}</div>
      ) : null}
    </div>
  );
}

function FactorRow({ factor }: { factor: MvpFactor }) {
  const t = useTranslations("TournamentStats.mvp");
  const tf = useTranslations("TournamentStats.mvp.factor");
  // Подписи нет — показываем сам ключ: выдуманное человеческое название скрыло бы,
  // что бэкенд добавил компоненту, о которой витрина не знает.
  const label = tf.has(factor.key) ? tf(factor.key) : factor.key;
  // Поля нет у старых ответов — тогда считаем компоненту измеренной.
  const measured = factor.measured !== false;
  const percent = Math.round(Math.min(Math.max(factor.value, 0), 1) * 100);

  return (
    <div className="grid grid-cols-[112px_1fr_34px] items-center gap-3">
      <span className="truncate text-xs text-muted-foreground">{label}</span>

      {measured ? (
        <span
          className="block h-2 overflow-hidden rounded-full bg-muted"
          role="presentation"
        >
          <span
            className="block h-full rounded-full"
            style={{ width: `${percent}%`, background: scoreColor(percent) }}
          />
        </span>
      ) : (
        /* Полосы нет вовсе: нарисованная полоса на подставленном значении — это
           утверждение о наблюдении, которого не было. */
        <span className="text-[11px] italic text-muted-foreground">
          {t("notMeasured")}
        </span>
      )}

      <span
        className={cn(
          "text-right font-heading text-[15px] font-semibold tabular-nums",
          !measured && "text-muted-foreground/50",
        )}
        title={
          measured
            ? undefined
            : t("notMeasuredHint")
        }
      >
        {measured ? percent : "—"}
      </span>
    </div>
  );
}

export function MvpCard({ mvp }: { mvp: TournamentMvp | null | undefined }) {
  // Хук ДО раннего выхода: React считает вызовы по порядку, и `useTranslations`
  // после `return null` уронил бы карточку при первом же появлении MVP.
  const t = useTranslations("TournamentStats.mvp");
  // Подписи мест берутся из словаря пьедестала, а не дублируются здесь: это те же
  // слова, и вторая копия однажды разойдётся с первой.
  const tPlace = useTranslations("TournamentStats.podium");

  if (!mvp) return null;

  // Балл нормирован в [0..1] — сумма весов равна единице.
  const score = Math.round(mvp.score * 100);
  const placeKey = mvp.placement ? PLACE_KEY[mvp.placement] : undefined;
  const place = placeKey ? tPlace(placeKey) : null;

  return (
    <div className="grid overflow-hidden rounded-2xl border border-border bg-card lg:grid-cols-[1.15fr_1fr]">
      <div className="flex flex-col gap-5 border-border p-[22px] lg:border-r">
        <SectionHeading title={t("title")} />

        <div className="flex flex-1 flex-wrap items-center gap-[18px]">
          <div className="flex min-w-0 items-start gap-4 lg:order-1">
            <Link href={`/u/${mvp.userId}`} className="shrink-0">
              <Avatar className="h-[52px] w-[52px]">
                <AvatarImage src={mvp.avatarUrl ?? undefined} alt={mvp.displayName} />
                <AvatarFallback className="bg-muted font-heading text-[21px] font-bold text-muted-foreground">
                  {mvp.displayName.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </Link>

            <div className="min-w-0">
              <Link
                href={`/u/${mvp.userId}`}
                className="block truncate font-heading text-[38px] font-bold leading-none tracking-[-0.01em] transition-colors hover:text-primary"
              >
                {mvp.displayName}
              </Link>

              <div className="mt-2 flex flex-wrap items-center gap-2">
                {mvp.teamName ? (
                  <span className="text-[13px] text-muted-foreground">
                    {mvp.teamName}
                  </span>
                ) : null}
                {place ? (
                  <span
                    className="rounded-md px-2 py-[3px] text-[10px] font-bold uppercase tracking-[0.08em]"
                    style={{
                      background: `color-mix(in srgb, ${MEDALS.gold.mid} 18%, transparent)`,
                      color: MEDALS.gold.mid,
                    }}
                  >
                    {place}
                  </span>
                ) : null}
                {/* Роль приходит с бэкенда сырым значением ("laner" / "jungle" /
                    "support") — до этой правки оно и показывалось: на русской
                    странице стоял бейдж «jungle». Незнакомое значение печатаем как
                    есть: выдуманная подпись скрыла бы, что бэкенд завёл новую роль. */}
                {mvp.role ? (
                  <span className="rounded-md bg-muted px-2 py-[3px] text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
                    {t.has(`role${mvp.role[0]!.toUpperCase()}${mvp.role.slice(1)}`)
                      ? t(`role${mvp.role[0]!.toUpperCase()}${mvp.role.slice(1)}`)
                      : mvp.role}
                  </span>
                ) : null}
                {mvp.isFormerMember ? (
                  <span className="rounded-md border border-border px-2 py-[3px] text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
                    {t("formerMember")}
                  </span>
                ) : null}
              </div>

              <div className="mt-4 flex flex-wrap items-start gap-x-[22px] gap-y-3">
                <Stat
                  label={t("kda")}
                  value={mvp.kdaRatio.toFixed(2)}
                  raw={mvp.avgKda}
                />
                <Stat
                  label={t("winrate")}
                  value={`${Math.round(mvp.winrate)}%`}
                  /* Склейка числа со словом ломается на склонениях («2 игр») и на
                     других языках. Ключ несёт ICU-plural, форма выбирается словарём. */
                  raw={t("record", {
                    wins: mvp.wins,
                    losses: mvp.losses,
                    games: mvp.games,
                  })}
                />
                {mvp.topChampionName ? (
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.09em] text-muted-foreground">
                      {t("topPick")}
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      <span className="relative h-8 w-8 overflow-hidden rounded-lg">
                        <ChampionIcon
                          name={mvp.topChampionName}
                          size={32}
                          className="size-full scale-[1.15] object-cover"
                        />
                      </span>
                      <span className="text-sm font-semibold">
                        {mvp.topChampionName}
                      </span>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="lg:order-2 lg:ml-auto">
            <ScoreRing score={score} />
          </div>
        </div>

        {mvp.isLowConfidence ? (
          <p className="border-t border-border pt-3 text-xs text-muted-foreground">
            {t("lowConfidence")}
          </p>
        ) : null}
      </div>

      {/* Именованная область, а не просто блок: разложение — самостоятельная часть
          карточки, и читалка должна уметь на неё перейти. Побочно это единственный
          надёжный способ отличить подпись компоненты «KDA» от показателя «KDA» в
          шапке — они называются одинаково по делу, а не по недосмотру. */}
      <section
        aria-label={t("breakdownTitle")}
        className="flex flex-col gap-3 bg-surface-inset p-[22px]"
      >
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
            {t("breakdownTitle")}
          </span>
          {/* Шкала подписана явно: без неё цифра читается как проценты побед. */}
          <span className="text-[11px] text-muted-foreground">0–100</span>
        </div>

        {/* Оговорка обязательна, а не украшение. Балл MVP собран из результата команды
            и вклада в матчи — место в турнире весит в нём больше всех прочих слагаемых.
            Поведенческий балл в таблице отвечает на другой вопрос: как игрок играл
            относительно своей роли, и результат команды в него не входит вовсе.
            Без этой строки читатель считает два числа одной величиной и ищет ошибку. */}
        <p className="text-[11px] leading-snug text-muted-foreground">
          {t("breakdownNote")}
        </p>

        <div className="flex flex-col gap-2.5">
          {mvp.factors.map((factor) => (
            <FactorRow key={factor.key} factor={factor} />
          ))}
        </div>
      </section>
    </div>
  );
}
